/**
 * Which room objects belong on a wall's elevation.
 *
 * ONE rule, shared by the on-screen ElevationsViewer (`useElevationsData`)
 * and the PDF elevation renderer (`renderElevationToImage`). The PDF path
 * used to carry its own copy with a 1 m axis-aligned proximity test, so in a
 * narrow bathroom the vanity and toilet appeared on BOTH long walls.
 *
 * Rule, in order:
 *   1. parent-wall match (`parentWallId` / `parentWallIds[0]`) → on this wall
 *   2. children of a wall-matched object (via `parents` / `children`) → on it
 *   3. a light HANGING over a free-standing leg → on both its faces
 *      (`hangsOverLeg`), checked before 4 because it is ceiling-mounted
 *   4. ceiling-mounted types → never on a wall elevation
 *   5. otherwise ground-mounted: on this wall only if the object's ORIENTED
 *      bounding box comes within `GROUND_MOUNTED_PROXIMITY_THRESHOLD` (4") of
 *      the wall plane AND overlaps the wall's span (10 cm margin)
 *   6. a FREE-STANDING face (an island / peninsula side) admits only what
 *      the LEG owns (`ownedByLeg`, which also skips step 5's proximity test)
 *      or names nobody, and only on THIS side of it (`onThisFaceOfLeg`) —
 *      unless it SPANS the leg (`spansLeg`), which a countertop does
 */
import * as THREE from 'three';
import { isStagingDecor } from '@shared-lib/staging';
import { getWallPosition, getWallNormal } from './elevationViewerHelpers';
import {
  isCeilingMounted,
  GROUND_MOUNTED_PROXIMITY_THRESHOLD,
} from '../constants/elevationConstants';
import { isHungCeilingLight } from './lightingClassification';
import { objectFootprint } from '@shared-lib/geometry/objectFootprint';
import { doPolygonsOverlap } from '@shared-lib/bathroom-analyzer/pipeline/geometry';

const toVector = (center) => {
  if (Array.isArray(center)) {
    return new THREE.Vector3(center[0], center[1], center[2]);
  }
  if (center && typeof center === 'object') {
    return new THREE.Vector3(center.x || 0, center.y || 0, center.z || 0);
  }
  return null;
};

const toEuler = (rotation) => {
  let x = 0;
  let y = 0;
  let z = 0;
  if (Array.isArray(rotation)) [x, y, z] = rotation;
  else if (rotation && typeof rotation === 'object') {
    x = rotation.x || 0;
    y = rotation.y || 0;
    z = rotation.z || 0;
  }
  // Auto-detect degrees
  if (Math.max(Math.abs(x), Math.abs(y), Math.abs(z)) > 2 * Math.PI) {
    x = THREE.MathUtils.degToRad(x);
    y = THREE.MathUtils.degToRad(y);
    z = THREE.MathUtils.degToRad(z);
  }
  return new THREE.Euler(x, y, z);
};

/** Object dimensions in meters (object, array, or inch-tagged forms). */
export const objectDimensionsMeters = (dimensions) => {
  const d = { width: 0.5, height: 0.5, depth: 0.5 };
  if (!dimensions) return d;
  if (Array.isArray(dimensions)) {
    d.width = dimensions[0] || 0.5;
    d.height = dimensions[1] || 0.5;
    d.depth = dimensions[2] || 0.5;
    return d;
  }
  if (typeof dimensions === 'object') {
    d.width = dimensions.width || dimensions.x || 0.5;
    d.height = dimensions.height || dimensions.y || 0.5;
    d.depth = dimensions.depth || dimensions.z || 0.5;
    if (dimensions.unit === 'inches' || d.width > 10) {
      d.width *= 0.0254;
      d.height *= 0.0254;
      d.depth *= 0.0254;
    }
  }
  return d;
};

const wallSize = (wall) => {
  const dims = wall.dimensions;
  if (dims && typeof dims === 'object' && !Array.isArray(dims)) {
    return { width: dims.x || 3, height: dims.y || 2.5 };
  }
  if (Array.isArray(dims))
    return { width: dims[0] || 3, height: dims[1] || 2.5 };
  return { width: 3, height: 2.5 };
};

const objectType = (obj) =>
  typeof obj.type === 'object' && obj.type
    ? Object.keys(obj.type)[0]
    : obj.type || obj.category;

/**
 * Is this object on the ISLAND / PENINSULA leg whose face is being drawn?
 *
 * Position cannot answer it. Step 4 measures `Math.abs(toObject · normal)` —
 * how far a thing is from the wall's PLANE, never which side of it — which is
 * right for a room wall (an object may sit a little inside one) and blind for
 * a leg, whose two faces ARE its own objects' outermost edges. And a leg's
 * synthesized wall runs the length of the SEGMENT, which reaches the room wall
 * even where its cabinets stop short: on Girdwood the east wall's own run
 * stands in that corner, overlapping the peninsula's footprint in plan.
 *
 * PARENTAGE answers it. The leg's own run emitted `legObjectIds` (resolved
 * through `runAnswersTo`, so an aliased run still matches), and the rest of
 * what it carries names the leg's inline cabinet wall in `cabinetWallRefs` —
 * its sink, dishwasher, faucet, back panel and seating overhang all do. A
 * thing naming a parent WALL is mounted on that wall and a thing carrying
 * another run's id belongs to that run, so neither is the leg's; a thing that
 * names nobody (the bar stools) is admitted on position, as it always was.
 *
 * What the leg OWNS also skips the 4" proximity test. That test asks how far
 * a thing stands from the wall it is against, and a leg's face is its outer
 * EDGE instead: the faucet sits 0.49 m behind the counter's front edge on
 * Girdwood's peninsula and was thrown off its own elevation by it.
 *
 * Returns the whole owned SET rather than answering per object, because
 * ownership descends and the descent needs the full list.
 */
const legOwnedIds = (seg, objects) => {
  const owned = new Set();
  const ids = new Set(seg.legObjectIds || []);
  objects.forEach((obj) => {
    if (ids.has(obj.id)) owned.add(obj.id);
    // `cabinetWallId` is null on a plain `freestanding` segment, and a ref
    // carrying no id would then match it — so require both.
    else if (
      seg.cabinetWallId &&
      obj.cabinetWallRefs?.some((ref) => ref?.id === seg.cabinetWallId)
    ) {
      owned.add(obj.id);
    }
  });

  // A CHILD of something the leg owns is the leg's as well, transitively.
  // This is the same descent step 2 already makes for a wall-matched object,
  // and without it a leg that names neither its fittings nor a cabinet wall
  // owns nothing but its own run: on the test island `cabinetWallId` is null
  // and `legObjectIds` lists the run's boxes and its countertop, so the sink
  // and the faucet — parented to that countertop — were owned by nobody. They
  // then had to pass the 4" proximity test from a face they sit 0.43 m behind,
  // and the FAUCET, being small, failed it and dropped off the drawing.
  return withDescendants(owned, objects);
};

/**
 * Close `owned` over `parents`: everything descended from an owned object,
 * transitively, joins it. Shared with the leg BUILDER
 * (`elevationFreestandingWalls`), which needs the same descent to size a
 * leg's body — a counter names no wall, only its boxes.
 *
 * @param {Set<string>} owned mutated and returned
 * @param {object[]} objects
 * @returns {Set<string>}
 */
export const withDescendants = (owned, objects) => {
  let grew = true;
  while (grew) {
    grew = false;
    objects.forEach((obj) => {
      if (owned.has(obj.id)) return;
      if (obj.parents?.some((p) => owned.has(p?.id))) {
        owned.add(obj.id);
        grew = true;
      }
    });
  }
  return owned;
};

/**
 * Does this pendant / chandelier hang over the leg whose face is being drawn?
 *
 * The pendants over an island belong on BOTH of its elevations — from either
 * side you see them hanging over the counter, and the elevation is where their
 * hang height is dimensioned. So this asks the one question that is true from
 * both sides: does the fixture's plan footprint sit over the leg's body?
 *
 * Nothing on the object records the counter it was hung over. The planner
 * (`resolvePendantAnchors`) knew, but wrote no link, and the fields a leg's
 * cabinetry uses to say so — `cabinetWallRefs`, `legObjectIds` — are never
 * stamped on a light. Geometry is therefore the only source, and the leg's
 * own `footprint` is exactly the right operand: it is the leg's BODY, both
 * halves of it, so the test is symmetric between Side A and Side B.
 *
 * Deliberately NOT the plane-distance test every other object gets: a pendant
 * hangs over the counter's middle, ~0.3-0.5 m from either face, which the 4"
 * threshold would reject from both sides — and `onThisFaceOfLeg` would then
 * award it to whichever side of the divider line it happened to fall on.
 */
const hangsOverLeg = (leg, obj) => {
  const body = leg?.footprint;
  if (!Array.isArray(body) || body.length < 3) return false;
  const footprint = objectFootprint(obj);
  if (!footprint) return false;
  return doPolygonsOverlap(footprint, body);
};

/** Does this object name an owner — a wall, a cabinet wall, or a run? */
const namesAnOwner = (obj) =>
  !!(
    obj.cabinetWallRefs?.length ||
    obj.parentWallId ||
    obj.parentWallIds?.[0] ||
    obj.runId
  );

/**
 * ...and is it on THIS FACE of that leg?
 *
 * Every object on a leg was placed against the leg's own LINE, at a signed
 * offset — and that line is not the leg's centreline: `dividerLine` puts it
 * at the front row's back face. So the sign of an object's offset from it is
 * the side it was built on, stated by whoever built it rather than guessed
 * from a distance. On Girdwood's peninsula: cabinets, countertop, sink and
 * dishwasher at +0.305 m, the faucet at +0.144, the back panel at −0.013, the
 * seating overhang at −0.178 and the stools at −0.394.
 *
 * This is what makes the two faces two DRAWINGS: the cook side gets the
 * doors, the sink and the faucet; the seating side gets the back panel, the
 * overhang and the stools — rather than both getting everything.
 */
/**
 * ...or does it span the leg, and belong to both faces?
 *
 * A countertop is not on a side. It is the slab over BOTH rows, and from
 * either face you are looking at its edge — which is the thing a builder
 * dimensions on a seating elevation. The same is true of anything else built
 * across a leg.
 *
 * The tie is what made this visible: the island counter's centre lands exactly
 * ON the divider line (`dividerLine` is the front row's back face, and the
 * slab is placed centred on it), `onThisFaceOfLeg` reads `perp >= 0` as side
 * A, and side B therefore never got it. Nudging the comparison would only move
 * the problem to the other face.
 *
 * Measured over every leg-owned object on dev — 368 of them — the share of an
 * object's own depth lying on the minority side of the line separates cleanly:
 *
 *   325 objects (cabinets, panels, hoods, sinks, stools)   < 0.001
 *   13 ranges, whose door or handle jut past the line       <= 0.044
 *   59 countertops                                  median 0.342, max 0.500
 *
 * Nothing at all falls between 0.044 and 0.30, so the threshold is not a
 * tuning knob; it separates "built across the leg" from "touches the line".
 */
const LEG_SPAN_MIN_SHARE = 0.1;

const spansLeg = (seg, obj) => {
  const line = seg?.line;
  if (!line) return false;
  const footprint = objectFootprint(obj);
  if (!footprint) return false;
  const perps = footprint.map(
    (c) =>
      (c.x - line.origin.x) * line.normal.x +
      (c.z - line.origin.z) * line.normal.z
  );
  const min = Math.min(...perps);
  const max = Math.max(...perps);
  const span = max - min;
  if (!(span > 0)) return false;
  const minoritySide = Math.max(0, Math.min(-min, max));
  return minoritySide / span >= LEG_SPAN_MIN_SHARE;
};

const onThisFaceOfLeg = (seg, objPos) => {
  const line = seg.line;
  if (!line) return true;
  const perp =
    (objPos.x - line.origin.x) * line.normal.x +
    (objPos.z - line.origin.z) * line.normal.z;
  return seg.side === 'A' ? perp >= 0 : perp < 0;
};

/**
 * @param {object} checkWall - the wall being drawn
 * @param {object[]} objects - candidate room objects
 * @returns {object[]} the subset that belongs on this wall's elevation
 */
export const objectsNearWall = (checkWall, objects) => {
  const leg = checkWall.freeSegment;
  // Once per call, not once per object: the descent closes over the whole list.
  const ownedIds = leg ? legOwnedIds(leg, objects) : null;
  const wallPos = getWallPosition(checkWall);
  const wallNormal = getWallNormal(checkWall);
  const { width: wallWidth, height: wallHeight } = wallSize(checkWall);
  const wallRight = new THREE.Vector3(1, 0, 0).applyEuler(
    toEuler(checkWall.rotation)
  );
  const wallUp = new THREE.Vector3(0, 1, 0);

  // 1. parent-wall match
  const wallMatchedIds = new Set(
    objects
      .filter(
        (obj) => (obj.parentWallId || obj.parentWallIds?.[0]) === checkWall.id
      )
      .map((obj) => obj.id)
  );

  // 2. children of wall-matched objects
  const childIds = new Set();
  objects.forEach((obj) => {
    if (obj.parents?.some((p) => wallMatchedIds.has(p.id)))
      childIds.add(obj.id);
    if (wallMatchedIds.has(obj.id) && Array.isArray(obj.children)) {
      obj.children.forEach((id) => childIds.add(id));
    }
  });

  return objects.filter((obj) => {
    if (!obj.center) return false;
    // Staged decor is DRESSING, never a drawn element. It has to be refused
    // here rather than left to `isGroundMounted` / `isWallMounted` below,
    // because a staged towel or print carries `parentWallIds` and so is
    // wall-matched at the short-circuit two lines down -- it never reaches
    // those predicates at all. The PDF and construction-document callers
    // (`pdfGenerator.js`, `elevationWallDataHelpers.js`) pass RAW room
    // objects with no visibility flags, so this is their only guard.
    if (isStagingDecor(obj)) return false;
    if (isHungCeilingLight(obj)) {
      return hangsOverLeg(checkWall.freeSegment, obj);
    }
    if (isCeilingMounted(objectType(obj))) return false;
    if (wallMatchedIds.has(obj.id) || childIds.has(obj.id)) return true;

    const objPos = toVector(obj.center);
    if (!objPos) return false;
    const dims = objectDimensionsMeters(obj.dimensions);

    // 4. oriented half-extent toward the wall plane
    const e = toEuler(obj.rotation);
    const ax = new THREE.Vector3(1, 0, 0).applyEuler(e);
    const ay = new THREE.Vector3(0, 1, 0).applyEuler(e);
    const az = new THREE.Vector3(0, 0, 1).applyEuler(e);
    const halfDepthAlongNormal =
      Math.abs(ax.dot(wallNormal)) * (dims.width / 2) +
      Math.abs(ay.dot(wallNormal)) * (dims.height / 2) +
      Math.abs(az.dot(wallNormal)) * (dims.depth / 2);

    const toObject = objPos.clone().sub(wallPos);
    const distanceToPlane =
      Math.abs(toObject.dot(wallNormal)) - halfDepthAlongNormal;
    const legOwned = !!ownedIds?.has(obj.id);
    if (!legOwned && distanceToPlane > GROUND_MOUNTED_PROXIMITY_THRESHOLD) {
      return false;
    }
    if (leg) {
      if (!legOwned && namesAnOwner(obj)) return false;
      if (!onThisFaceOfLeg(leg, objPos) && !spansLeg(leg, obj)) return false;
    }

    const localX = toObject.dot(wallRight);
    const localY = toObject.dot(wallUp);
    const margin = 0.1;
    return (
      Math.abs(localX) <= wallWidth / 2 + dims.width / 2 + margin &&
      Math.abs(localY) <= wallHeight / 2 + dims.height / 2 + margin
    );
  });
};

/**
 * A wall's draw list with a live cabinet-run preview substituted in.
 *
 * A run drag re-solves the whole run every frame, so the ghost is not "the
 * same objects moved" — `materializeRunObjects` regenerates the run's boxes
 * and their ids with them, and a re-pack can add a filler or drop one. That is
 * why this hides by `hideIds` and re-associates the preview set from scratch
 * rather than merging by id, exactly as the floor plan's ghost layer does.
 *
 * Association mirrors `useElevationsData`'s own loop — `objectsNearWall` per
 * member wall of a group, first match wins — so a previewed object lands on
 * the same wall the committed one will. Cheap enough for a drag frame because
 * it runs over ONE wall's group and the run's own objects, not the room.
 *
 * @param {Object} wallData    the wall being drawn (possibly grouped).
 * @param {Array}  nearbyObjects  its committed draw list.
 * @param {{objects: Array, hideIds: Set<string>}|null} preview
 * @returns {Array} the draw list; `nearbyObjects` UNCHANGED when there is no
 *   preview, so a non-dragging render keeps its array identity.
 */
export const objectsWithRunPreview = (wallData, nearbyObjects, preview) => {
  if (!preview?.objects) return nearbyObjects;
  const hidden = preview.hideIds || new Set();
  const kept = nearbyObjects.filter((o) => !hidden.has(o.id));
  const walls = wallData?.isGrouped ? wallData.groupedWalls || [] : [wallData];
  const seen = new Set(kept.map((o) => o.id));
  const added = [];
  walls.forEach((checkWall, wallIndex) => {
    if (!checkWall) return;
    objectsNearWall(checkWall, preview.objects).forEach((obj) => {
      if (seen.has(obj.id)) return;
      seen.add(obj.id);
      added.push({
        ...obj,
        roomId: obj.roomId,
        associatedWallIndex: wallIndex,
        associatedWall: checkWall,
      });
    });
  });
  return [...kept, ...added];
};
